import { IScope, IPromise, ITimeoutService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IInsightboard, IInsightboardPermissions } from "../models/models";
import { ITag } from "@gtmhub/tags/models";
import { ICheckAccessFactory } from "@gtmhub/shared/factories/check-permissions";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { InsightboardService } from "../services/dashboardService";
import { IIndicator } from "@gtmhub/error-handling";
import { IAccess } from "@gtmhub/sessions/models/models";
import { IAccessChecked } from "@gtmhub/shared/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { storage } from "@gtmhub/core/storage";
import { getCurrentUserId } from "@gtmhub/users";

export type CreateInsightboardIndicators = {
  saving?: IIndicator;
  savingTag?: IIndicator;
};

export interface ICreateInsightboardCtrlScope extends IScope {
  currentState: string;
  indicators: CreateInsightboardIndicators;
  insightboard: IInsightboard;
  originalInsightboard: IInsightboard;
  permissions: IInsightboardPermissions;
  permissionsType: {
    custom: boolean;
    public: boolean;
  };
  suggestedTags: ITag[];
  tagExists: boolean;
  invalidTag: boolean;
  addTag(tag: ITag): void;
  handleTagAdded(tag: ITag): void;
  handleTagAdding(): void;
  handleInvalidTag(): void;
  changePermissionsType(type: string);
  save(): void;
  showSuggestedTags(): boolean;
  showTagErrorMessage(): void;
  showTagInSuggestedTagList(tag: ITag): void;
  setInsightboardAccess(access: IAccess): void;
  resetIndicator(indicator: keyof CreateInsightboardIndicators): void;
}

export class CreateInsightboardCtrl {
  public static $inject = ["$rootScope", "$scope", "$state", "$timeout", "CheckAccess", "CurrentUserService", "InsightboardService"];

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: ICreateInsightboardCtrlScope,
    private $state: StateService,
    private $timeout: ITimeoutService,
    private checkAccess: ICheckAccessFactory,
    private currentUserService: CurrentUserService,
    private insightboardService: InsightboardService
  ) {
    this.init();
  }

  private defaultCustomAccess: IAccess = {
    inherits: false,
    permissions: [
      {
        grant: {
          general: ["read", "update", "delete", "create", "modifyPermissions"],
        },
        principalId: getCurrentUserId(),
        principalKind: "user",
      },
      {
        grant: {
          general: ["read"],
        },
        principalId: storage.get("accountId"),
        principalKind: "account",
      },
    ],
  };

  private publicAccess: IAccess = {
    inherits: true,
    permissions: [],
  };

  private strings = {
    insightboardParams: "insightboardParams",
    createState: "gtmhub.insightboards._form.create",
  };

  private init = (): void => {
    this.$scope.indicators = {};
    this.$scope.addTag = this.addTag;
    this.$scope.handleTagAdded = this.handleTagAdded;
    this.$scope.handleTagAdding = this.handleTagAdding;
    this.$scope.handleInvalidTag = this.handleInvalidTag;
    this.$scope.changePermissionsType = this.changePermissionsType;
    this.$scope.currentState = this.$state.current.name === this.strings.createState ? "createForm" : "editForm";
    this.$scope.save = this.save;
    this.$scope.showSuggestedTags = this.showSuggestedTags;
    this.$scope.showTagErrorMessage = this.showTagErrorMessage;
    this.$scope.showTagInSuggestedTagList = this.showTagInSuggestedTagList;
    this.$scope.resetIndicator = this.resetIndicator;

    this.getSuggestedTags();

    this.$scope.insightboard = {
      access: angular.copy(this.defaultCustomAccess),
      name: null,
      tags: [],
      tag: null,
    };

    this.$scope.permissionsType = {
      custom: true,
      public: false,
    };

    this.$scope.originalInsightboard = {
      access: angular.copy(this.publicAccess),
      name: null,
      tag: null,
    };

    const canManageBoards: boolean = this.$rootScope.hasPermission("ManageData") ? true : false;

    // using this scope variable with the same name as in edit-insightboard.ts because the view is shared
    this.$scope.permissions = {
      form: canManageBoards,
      modifyName: canManageBoards,
      modifyPermissions: canManageBoards,
    };

    this.$scope.setInsightboardAccess = this.setInsightboardAccess;
  };

  private handleTagAdding = (): void => {
    this.$scope.indicators.savingTag = { progress: true };
  };

  private handleTagAdded = (tag: ITag) => {
    this.resetIndicator("savingTag");

    if (!tag.name) {
      const index = this.$scope.insightboard.tags.length - 1;
      this.$scope.insightboard.tags[index].name = tag.title.toLocaleLowerCase();
      this.$scope.insightboard.tags[index].title = tag.title;
    }
  };

  private handleInvalidTag = (): void => {
    this.resetIndicator("savingTag");
  };

  private showTagErrorMessage = () => {
    this.$scope.tagExists = true;
    this.$timeout(() => {
      this.$scope.tagExists = false;
    }, 2000);
  };

  private getSuggestedTags = () => {
    const insightboardsTagsMap = this.currentUserService.getUserSetting("insightboardParams");

    this.$scope.suggestedTags = [];
    if (insightboardsTagsMap && insightboardsTagsMap.tags) {
      Object.keys(insightboardsTagsMap.tags).forEach((key) => {
        const tag: ITag = {
          name: key,
          title: insightboardsTagsMap.tags[key],
          selected: false,
        };
        this.$scope.suggestedTags.push(tag);
      });
    }
  };

  private showTagInSuggestedTagList = (tag: ITag): void => {
    for (let i = 0; i < this.$scope.suggestedTags.length; i++) {
      const suggestedTag = this.$scope.suggestedTags[i];
      if (suggestedTag.title.toLowerCase() === tag.title.toLowerCase()) {
        suggestedTag.selected = false;
        break;
      }
    }
  };

  private changePermissionsType = (type: string): void => {
    switch (type) {
      case "custom":
        this.setInsightboardAccess(angular.copy(this.defaultCustomAccess));

        this.$scope.permissionsType = {
          custom: true,
          public: false,
        };
        break;

      case "public":
        this.setInsightboardAccess(angular.copy(this.publicAccess));

        this.$scope.permissionsType = {
          public: true,
          custom: false,
        };
        break;

      default:
        break;
    }
  };

  private addTag = (tag: ITag): void => {
    this.$scope.tagExists = false;

    if (this.$scope.insightboard.tags.some((existingTag) => existingTag.title.toLowerCase() === tag.title.toLowerCase())) {
      this.$scope.tagExists = true;
      this.$timeout(() => {
        this.$scope.tagExists = false;
      }, 2000);
    } else {
      const newTag = {
        name: tag.title.toLowerCase(),
        title: tag.title,
      };
      this.$scope.insightboard.tags.push(newTag);
    }
  };

  private save = (): void => {
    this.checkAccess.checkAccess(this.$scope.insightboard.access).then((accessChecked: IAccessChecked) => {
      if (accessChecked.canSave) {
        const access = this.includeSubteamsPermissions(accessChecked.access);
        return this.createInsightboard(access);
      }
    });
  };

  private setInsightboardAccess = (access: IAccess): void => {
    this.$scope.insightboard.access = access;
  };

  private createInsightboard = (access: IAccess): IPromise<void> => {
    const newInsightboard: IInsightboard = {
      name: this.$scope.insightboard.name,
      tag: Math.random().toString(36).substr(2, 10),
      access: access,
      tags: this.$scope.insightboard.tags,
    };

    this.$scope.indicators.saving = { progress: true };
    return this.insightboardService.createInsightboard(newInsightboard).then(
      (result) => {
        this.$scope.resetIndicator("saving");
        this.$scope.invalidTag = false;

        this.$state.go("gtmhub.insightboard", { dashboardId: result.id });
      },
      (error) => {
        if (error.data.includes("invalid tag")) {
          this.$scope.invalidTag = true;
          this.$scope.resetIndicator("saving");
        } else {
          this.$scope.indicators.saving = { error };
        }
      }
    );
  };

  private showSuggestedTags = (): boolean => {
    return this.$scope.suggestedTags.some((tag) => tag.selected === false);
  };

  private includeSubteamsPermissions = (access: IAccess): IAccess => {
    if (access == null) {
      return access;
    }

    const newAccess = angular.copy(access);
    const parentsWithIncludedSubteams = this.getTeamsPermissionsWithIncludedSubteams(newAccess);
    if (parentsWithIncludedSubteams.length === 0) {
      return access;
    }

    parentsWithIncludedSubteams.forEach((p) => (p.principalKind = "team_and_subteams"));

    if (newAccess.permissions.length > 0) {
      newAccess.inherits = false;
    }

    return newAccess;
  };

  private getTeamsPermissionsWithIncludedSubteams = (access: IAccess) => {
    return access.permissions.filter((permission) => {
      return permission.principalKind === "team" && permission.includeSubteams;
    });
  };

  private resetIndicator = (indicator: keyof CreateInsightboardIndicators): void => {
    delete this.$scope.indicators[indicator];
  };
}
